/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * appVersionOverride — DEBUG-ONLY runtime web-app version override.
 *
 * ## The mechanism this hooks into
 *
 * The version a launch runs as is carried by the `x-app-version` cookie on the
 * Web App's own origin (see `setAppVersionCookie()`). The serving layer reads
 * that cookie **on the HTTP request for the document and its assets** and
 * resolves them against the matching build; with no cookie it serves the
 * default/latest build. Nothing in the running page consults the cookie — by
 * the time JS executes, the build has already been chosen.
 *
 * Normally the value comes from the shell app, which appends
 * `?x-app-version=v1.0.461` to the iframe URL; `bootstrap()` in `index.tsx`
 * mirrors that onto the cookie on every launch.
 *
 * ## Why the override needs to persist
 *
 * Two consequences follow from "the cookie is read per request, not by the
 * page":
 *
 *   1. Writing the cookie is not enough on its own — a full document
 *      navigation has to follow, so a *new* request carries it. That is
 *      `reloadApplicationAtRoot()`.
 *   2. The override has to survive that navigation and win over the URL param,
 *      because `reloadApplicationAtRoot()` deliberately keeps the query string.
 *      Without step 2 the reloaded document would arrive as the requested
 *      build and then immediately rewrite the cookie back to the shell's
 *      version from the URL — leaving later chunk requests resolving against a
 *      *different* build than the one that loaded, which is exactly the
 *      mismatched-chunk failure the whole flow exists to avoid.
 *
 * So the requested version is persisted to `localStorage` (synchronous, so it
 * is readable at the top of `bootstrap()` before anything renders) and
 * `resolveLaunchAppVersion()` gives it precedence over the URL param. The
 * cookie itself stays the single source of truth for version routing — this
 * module only decides which value goes into it.
 *
 * ## Gating
 *
 * Everything here is behind the `__DEV_TOOLS_ENABLED__` DefinePlugin flag
 * (webpack.config.js, true for debug builds only). In a release build
 * `isDevToolsBuild()` compiles to a constant `false`, so every entry point
 * below is inert: `readAppVersionOverride()` returns `null`, both mutators
 * return `false` without touching storage or the cookie, and `bootstrap()`'s
 * version resolution behaves exactly like today's bare `getAppVersionFromUrl()`
 * call.
 *
 * Note this gates *behaviour*, not bytes: Terser does not propagate the
 * constant across module boundaries, so these functions are still present in
 * the release bundle, just unreachable in effect. Same property as
 * `mockOverride`. If the code itself ever has to be absent from a release
 * artefact, the tool for that is the `__START_DEBUG_CODE_CLEANUP__` /
 * `__END_DEBUG_CODE_CLEANUP__` source-strip in prebuild.sh, not this flag.
 */

import { Log } from "../model/logging/Log";
import {
  clearAppVersionCookie,
  getAppVersionFromUrl,
  isValidAppVersion,
  setAppVersionCookie
} from "../model/utility";

declare const __DEV_TOOLS_ENABLED__: boolean;

const TAG: string = "appVersionOverride";

/**
 * localStorage key holding the developer-requested version. Deliberately NOT a
 * cookie: a second version cookie would compete with `x-app-version` for the
 * serving layer's attention. This records the *intent*; the cookie remains the
 * only thing the CDN ever sees.
 */
export const APP_VERSION_OVERRIDE_STORAGE_KEY: string = "OVERRIDE_APP_VERSION";

/** True only in debug builds where the DefinePlugin flag is set to true. */
export function isDevToolsBuild(): boolean {
  try {
    return (
      typeof __DEV_TOOLS_ENABLED__ !== "undefined" &&
      __DEV_TOOLS_ENABLED__ === true
    );
  } catch {
    return false;
  }
}

function hasBrowserStorage(): boolean {
  return (
    typeof window !== "undefined" && typeof window.localStorage !== "undefined"
  );
}

/**
 * The version the developer asked for, or `null` when no override is active,
 * this is a release build, storage is unavailable, or the stored value no
 * longer passes validation (hand-edited storage, a format change since it was
 * written). An unusable value is treated as absent rather than repaired.
 */
export function readAppVersionOverride(): string | null {
  if (!isDevToolsBuild() || !hasBrowserStorage()) {
    return null;
  }
  try {
    const raw: string | null = window.localStorage.getItem(
      APP_VERSION_OVERRIDE_STORAGE_KEY
    );
    if (raw === null) {
      return null;
    }
    const version: string = raw.trim();
    return isValidAppVersion(version) ? version : null;
  } catch {
    return null;
  }
}

/**
 * The version this launch should run as: the debug override when one is
 * active, otherwise the shell's `x-app-version` query param exactly as before.
 *
 * Called once by `bootstrap()`. In a release build `readAppVersionOverride()`
 * is a constant `null`, so this is `getAppVersionFromUrl()` and nothing else.
 */
export function resolveLaunchAppVersion(): string | null {
  const override: string | null = readAppVersionOverride();
  if (override !== null) {
    Log.i(TAG, "resolveLaunchAppVersion() — debug override active");
    return override;
  }
  return getAppVersionFromUrl();
}

/**
 * Persists `version` as the override and writes it to the existing app version
 * cookie, so the next document request resolves against that build.
 *
 * Returns `false` — writing nothing, and leaving the currently active version
 * untouched — when the value fails `isValidAppVersion()`, when this is not a
 * debug build, or when storage is unavailable. A `false` return means the
 * caller must NOT reload: there is nothing new to load.
 *
 * The caller is responsible for the reload itself (see
 * `reloadApplicationAtRoot()`); this function only moves the cookie, and the
 * cookie only takes effect on the next request.
 */
export function applyAppVersionOverride(version: string): boolean {
  if (!isDevToolsBuild()) {
    return false;
  }
  const trimmedVersion: string = version.trim();
  if (!isValidAppVersion(trimmedVersion)) {
    Log.w(TAG, "applyAppVersionOverride() — rejected invalid version");
    return false;
  }
  if (!hasBrowserStorage()) {
    Log.w(TAG, "applyAppVersionOverride() — localStorage unavailable");
    return false;
  }
  try {
    window.localStorage.setItem(
      APP_VERSION_OVERRIDE_STORAGE_KEY,
      trimmedVersion
    );
  } catch (error: unknown) {
    Log.e(TAG, "applyAppVersionOverride()", error);
    return false;
  }
  setAppVersionCookie(trimmedVersion);
  Log.i(TAG, "applyAppVersionOverride() — version set to " + trimmedVersion);
  return true;
}

/**
 * Drops the override and expires the cookie, returning the app to the default
 * behaviour: the next document request carries no `x-app-version`, so the
 * serving layer picks the default/latest build, and the launch after that
 * re-derives the cookie from the shell's query param exactly as it does today.
 *
 * Returns `false` in a release build or when storage is unavailable — again,
 * the signal not to reload.
 */
export function clearAppVersionOverride(): boolean {
  if (!isDevToolsBuild()) {
    return false;
  }
  if (hasBrowserStorage()) {
    try {
      window.localStorage.removeItem(APP_VERSION_OVERRIDE_STORAGE_KEY);
    } catch (error: unknown) {
      Log.e(TAG, "clearAppVersionOverride()", error);
      return false;
    }
  }
  clearAppVersionCookie();
  Log.i(TAG, "clearAppVersionOverride() — override removed");
  return true;
}
